import {
  ArquivoPropostoSchema,
  LIMITES_TOOLKIT,
  ManifestoRotinaSchema,
  SlugToolkitSchema,
  type ClasseDeEfeito,
} from '@remoto/protocolo';
import { z } from 'zod';
import type { EstadoNoAlvo } from './estado';

/**
 * O contrato REST do toolkit (F3 da revisão de out/2026, ADR-0026): os corpos
 * que a tela manda, validados na borda (`api.md` 1), e as formas que a API
 * devolve. O manifesto e os arquivos são os MESMOS do canal do daemon
 * (`@remoto/protocolo`): uma rotina criada pela tela e uma proposta da mestre
 * passam pela mesma régua.
 *
 * Um HUMANO criando ou editando pela tela é proposta já aprovada por ele (D19,
 * D20: OPERADOR+). A mestre propõe pelo canal, e quem decide é a tela.
 */

const Motivo = z.string().trim().min(1).max(LIMITES_TOOLKIT.motivo);
const Descricao = z.string().trim().min(1).max(LIMITES_TOOLKIT.descricao);

/** O projeto de uma rotina ou verbete (ADR-0020): a chave que o servidor listou. */
export const ProjetoToolkitSchema = z.object({
  maquinaId: z.string().min(1),
  cwdProjeto: z.string().min(1).max(1_000),
});

export const CriarAlvoSchema = z.object({
  chave: SlugToolkitSchema,
  titulo: z.string().trim().min(1).max(200),
  sistema: z.string().trim().min(1).max(100),
  familia: z.string().trim().min(1).max(100).optional(),
  grau: z.string().trim().min(1).max(40).optional(),
});
export type CriarAlvo = z.infer<typeof CriarAlvoSchema>;

export const AtualizarAlvoSchema = z
  .object({ titulo: z.string().trim().min(1).max(200).optional(), ativo: z.boolean().optional() })
  .refine((a) => a.titulo !== undefined || a.ativo !== undefined, 'nada a atualizar');
export type AtualizarAlvo = z.infer<typeof AtualizarAlvoSchema>;

/** O pacote de uma versão: os mesmos tetos do canal; o hash é do servidor. */
const Pacote = {
  descricao: Descricao,
  manifesto: ManifestoRotinaSchema,
  arquivos: z.array(ArquivoPropostoSchema).min(1).max(LIMITES_TOOLKIT.arquivosPorVersao),
  motivo: Motivo,
};

/** Criar uma rotina pela tela: a rotina, a versão 1 e as extensões, já aprovadas. */
export const CriarRotinaSchema = z.object({
  nome: SlugToolkitSchema,
  titulo: z.string().trim().min(1).max(200),
  forma: z.enum(['FUNCAO', 'CRUD', 'VARIANTE', 'ROTEIRO']),
  entidade: z.string().trim().min(1).max(100).optional(),
  operacao: z.string().trim().min(1).max(100).optional(),
  paiId: z.string().min(1).optional(),
  projeto: ProjetoToolkitSchema.optional(),
  /** Os alvos em que ela vale desde o nascimento: extensões aprovadas por quem criou. */
  alvos: z.array(SlugToolkitSchema).max(50).default([]),
  ...Pacote,
});
export type CriarRotina = z.infer<typeof CriarRotinaSchema>;

/**
 * Uma versão nova pela tela. Voltar atrás é ISTO com o manifesto de uma antiga
 * e `substituiId` apontando a que ela supera (R5): o histórico não se reescreve.
 */
export const NovaVersaoSchema = z.object({ ...Pacote, substituiId: z.string().min(1).optional() });
export type NovaVersao = z.infer<typeof NovaVersaoSchema>;

/** Estender a um alvo, com a semelhança declarada por extenso (D31). */
export const NovaExtensaoSchema = z.object({
  alvo: SlugToolkitSchema,
  semelhanca: z.string().trim().min(1).max(2_000),
});
export type NovaExtensao = z.infer<typeof NovaExtensaoSchema>;

/** Marcar (ou desmarcar) a rotina como quebrada num alvo: sempre com motivo. */
export const MarcacaoSchema = z.object({
  alvo: SlugToolkitSchema,
  marca: z.enum(['QUEBRADA', 'REVISADA']),
  motivo: Motivo,
});
export type Marcacao = z.infer<typeof MarcacaoSchema>;

/** Decidir uma proposta pendente (D19, D20). Recusar exige motivo. */
export const DecisaoToolkitSchema = z
  .object({
    especie: z.enum(['versao', 'extensao', 'verbete']),
    decisao: z.enum(['APROVADA', 'RECUSADA']),
    motivo: z.string().trim().max(LIMITES_TOOLKIT.motivo).optional(),
  })
  .refine((d) => d.decisao !== 'RECUSADA' || (d.motivo ?? '').length > 0, {
    message: 'recusar exige motivo',
    path: ['motivo'],
  });
export type DecisaoToolkit = z.infer<typeof DecisaoToolkitSchema>;

export const ArquivarSchema = z.object({ arquivada: z.boolean() });
export type Arquivar = z.infer<typeof ArquivarSchema>;

/** Os filtros da lista de rotinas (query string). */
export const FiltroRotinasSchema = z.object({
  texto: z.string().trim().max(LIMITES_TOOLKIT.consulta).optional(),
  forma: z.enum(['FUNCAO', 'CRUD', 'VARIANTE', 'ROTEIRO']).optional(),
  alvo: SlugToolkitSchema.optional(),
  /** Na query string vem texto: só `'true'` liga. */
  arquivadas: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type FiltroRotinas = z.infer<typeof FiltroRotinasSchema>;

const CamposDoVerbete = {
  texto: Descricao,
  alvos: z.array(SlugToolkitSchema).max(50).default([]),
  rotinas: z.array(SlugToolkitSchema).max(50).default([]),
  autorDaOrdem: z.string().trim().min(1).max(200).optional(),
  dataDaOrdem: z.iso.date().optional(),
  motivo: Motivo,
};

export const CriarVerbeteSchema = z.object({
  termo: z.string().trim().min(1).max(200),
  classe: z.enum(['PORTA', 'LIMITE', 'ORDEM', 'ROTEIRO', 'RECEITA', 'OUTRO']),
  projeto: ProjetoToolkitSchema.optional(),
  ...CamposDoVerbete,
});
export type CriarVerbete = z.infer<typeof CriarVerbeteSchema>;

export const NovaVersaoVerbeteSchema = z.object({ ...CamposDoVerbete, substituiId: z.string().min(1).optional() });
export type NovaVersaoVerbete = z.infer<typeof NovaVersaoVerbeteSchema>;

/* -------------------------------------------------------------- respostas */

export interface AlvoDoToolkit {
  id: string;
  chave: string;
  titulo: string;
  sistema: string;
  familia: string | null;
  grau: string | null;
  ativo: boolean;
}

/** A célula da matriz rotina × alvo: o estado e as duas últimas evidências. */
export interface CelulaDaMatriz {
  alvo: string;
  estado: EstadoNoAlvo;
  /** A última validação REAL da vigente ali (data e resultado), ou nada. */
  ultimaReal: { em: string; resultado: string } | null;
  /** A última SONDA da vigente ali, ou nada (R16: "sem sonda" aparece na tela). */
  ultimaSonda: { em: string; resultado: string } | null;
}

export interface RotinaResumo {
  id: string;
  nome: string;
  titulo: string;
  forma: 'FUNCAO' | 'CRUD' | 'VARIANTE' | 'ROTEIRO';
  projeto: { maquinaId: string; cwdProjeto: string } | null;
  arquivada: boolean;
  vigente: { id: string; numero: number; sha256: string } | null;
  /** Classes de efeito das fases da vigente, para o filtro e o selo. */
  classes: ClasseDeEfeito[];
  /** Propostas sem decisão (versões e extensões) — destacadas na lista. */
  propostasPendentes: number;
  matriz: CelulaDaMatriz[];
}

export interface VersaoDaRotina {
  id: string;
  numero: number;
  sha256: string;
  descricao: string;
  manifesto: unknown;
  motivo: string;
  criadoEm: string;
  substituiId: string | null;
  propostaPor: { usuarioId: string | null; sessaoId: string | null };
  /** A última decisão (append-only: a que vale), ou `null` se pendente. */
  decisao: { decisao: 'APROVADA' | 'RECUSADA'; porId: string; em: string; motivo: string | null } | null;
  /** A versão que a superou, se alguma (R5: a superada fica com o motivo ao lado). */
  superadaPor: { id: string; numero: number; motivo: string } | null;
  arquivos: { caminho: string; sha256: string; bytes: number }[];
}

export interface RotinaDetalhe extends RotinaResumo {
  entidade: string | null;
  operacao: string | null;
  paiId: string | null;
  versoes: VersaoDaRotina[];
  extensoes: {
    id: string;
    alvo: string;
    semelhanca: string;
    criadoEm: string;
    decisao: { decisao: 'APROVADA' | 'RECUSADA'; porId: string; em: string; motivo: string | null } | null;
  }[];
  /** Os arquivos da VIGENTE, com conteúdo, para o editor da tela. */
  arquivosDaVigente: { caminho: string; sha256: string; bytes: number; conteudo: string }[];
}

/** Uma proposta pendente, com a VIGENTE ao lado para a tela montar o diff. */
export type PropostaPendente =
  | {
      especie: 'versao';
      id: string;
      rotina: { id: string; nome: string; titulo: string };
      proposta: VersaoDaRotina & { arquivosComConteudo: { caminho: string; conteudo: string }[] };
      vigente: (VersaoDaRotina & { arquivosComConteudo: { caminho: string; conteudo: string }[] }) | null;
    }
  | {
      especie: 'extensao';
      id: string;
      rotina: { id: string; nome: string; titulo: string };
      alvo: string;
      semelhanca: string;
      criadoEm: string;
      propostaPor: { usuarioId: string | null; sessaoId: string | null };
    }
  | {
      especie: 'verbete';
      id: string;
      verbete: { id: string; numero: number; termo: string };
      proposta: { texto: string; motivo: string; criadoEm: string };
      vigente: { texto: string } | null;
      propostaPor: { usuarioId: string | null; sessaoId: string | null };
    };

export interface VerbeteResumo {
  id: string;
  numero: number;
  termo: string;
  classe: 'PORTA' | 'LIMITE' | 'ORDEM' | 'ROTEIRO' | 'RECEITA' | 'OUTRO';
  projeto: { maquinaId: string; cwdProjeto: string } | null;
  arquivado: boolean;
  vigente: {
    id: string;
    numero: number;
    texto: string;
    alvos: string[];
    rotinas: string[];
    autorDaOrdem: string | null;
    dataDaOrdem: string | null;
  } | null;
  propostasPendentes: number;
}

/**
 * As frases pt-BR dos códigos do toolkit (`web.md`: a tela decide pelo CÓDIGO).
 * `Record` sobre a lista fechada: código novo sem frase não compila.
 */
export const FRASES_DOS_ERROS_DO_TOOLKIT: Record<
  | 'toolkit_nome_em_uso'
  | 'toolkit_alvo_desconhecido'
  | 'toolkit_proposta_ja_decidida'
  | 'toolkit_pacote_grande_demais'
  | 'toolkit_entrada_fora_do_pacote'
  | 'toolkit_caminho_repetido'
  | 'toolkit_projeto_fora_do_alcance'
  | 'toolkit_so_mestre_propoe'
  | 'toolkit_sessao_sem_criador'
  | 'toolkit_fase_desconhecida'
  | 'toolkit_hash_divergente'
  | 'toolkit_maquina_fora_do_onde'
  | 'toolkit_arquivada'
  | 'rotina_sem_versao_aprovada'
  | 'rotina_quebrada_no_alvo'
  | 'rotina_nao_se_aplica_ao_alvo'
  | 'so_leitura_neste_estado'
  | 'ato_externo_fora_da_fase_1',
  string
> = {
  toolkit_nome_em_uso: 'Já existe um item com este nome no toolkit.',
  toolkit_alvo_desconhecido: 'Este alvo não está no catálogo. Cadastre o alvo antes.',
  toolkit_proposta_ja_decidida: 'Esta proposta já foi decidida.',
  toolkit_pacote_grande_demais: 'O pacote passou do tamanho máximo. Nada foi cortado: reduza e envie de novo.',
  toolkit_entrada_fora_do_pacote: 'O arquivo de entrada do manifesto não está entre os arquivos enviados.',
  toolkit_caminho_repetido: 'Dois arquivos do pacote têm o mesmo caminho.',
  toolkit_projeto_fora_do_alcance: 'Você não tem acesso a este projeto.',
  toolkit_so_mestre_propoe: 'Só a sessão mestre propõe rotinas e verbetes.',
  toolkit_sessao_sem_criador: 'Esta sessão não tem um dono ativo, e o toolkit não sabe o que ela pode ver.',
  toolkit_fase_desconhecida: 'Esta fase não existe na versão vigente da rotina.',
  toolkit_hash_divergente: 'O pacote que rodou não é o da versão aprovada. Nada foi registrado.',
  toolkit_maquina_fora_do_onde: 'Esta rotina não declara rodar nesta máquina.',
  toolkit_arquivada: 'Este item está arquivado e só pode ser lido.',
  rotina_sem_versao_aprovada: 'A rotina ainda não tem versão aprovada.',
  rotina_quebrada_no_alvo: 'A rotina está marcada como quebrada neste alvo.',
  rotina_nao_se_aplica_ao_alvo: 'A rotina não foi estendida a este alvo.',
  so_leitura_neste_estado: 'Neste alvo a rotina só roda fases de leitura até ter um caso real conferido.',
  ato_externo_fora_da_fase_1: 'Ato externo (protocolar, assinar, mandar ao cliente) ainda não roda pelo toolkit.',
};
